import { formatDistanceToNow } from "date-fns";

import { Tag } from "@certa/catalyst/components/Tag";
import { DataObjectNameCell } from "../../components/DataObject/DataObjectTableCells/DataObjectNameCell";
import { DataObjectTableFieldsCell } from "../../components/DataObject/DataObjectTableCells/DataObjectTableFieldsCell";

import type {
  DataObjectInstance,
  DataObjectType,
  FiltersByFieldMap,
  GetDataObjectListObjectType,
  GetDataObjectListResult
} from "@certa/queries/types/dataObjectsAPIs.types";
import type { DataObjectUISchemaRaw } from "@certa/types/src/dataObjects.types";
import { getDateFnsLocale } from "../date/localeMap";
import {
  FILTER_TYPES,
  type SortDirection,
  type TableUnifiedColumn
} from "@certa/datagrid";
import { BadgesGroup } from "../../components/BadgesGroup";
import type { AppliedBadge, Badge, DateRangeValuesType } from "@certa/types";

const SORTABLE_TYPES = ["number", "integer", "date", "datetime"];

export type DefaultColumnType = "name" | "status" | "created_at" | "updated_at";

export const DEFAULT_INCLUDED_COLUMNS = [
  "name",
  "status",
  "created_at",
  "updated_at"
] as readonly DefaultColumnType[];

export const getDateTimeRangeValues = (
  value: DateRangeValuesType,
  fieldType: string
) => {
  return {
    ...value,
    is_date_time: fieldType === "datetime"
  };
};

export const getDataObjectTableCell = (key: string, data: any) => {
  switch (key) {
    case "createdAt":
      return data.createdAt
        ? formatDistanceToNow(new Date(data.createdAt), {
            addSuffix: true,
            locale: getDateFnsLocale()
          })
        : null;
    case "updatedAt":
      return data.updatedAt
        ? formatDistanceToNow(new Date(data.updatedAt), {
            addSuffix: true,
            locale: getDateFnsLocale()
          })
        : null;
    case "status":
      return data.status ? (
        <Tag
          label={data.status.name}
          backgroundColor={`#${data.status.colorCode}`}
          hasBorder={false}
        >
          {data.status.name}
        </Tag>
      ) : null;
    case "name":
      return (
        <DataObjectNameCell
          tag={data.objectType?.tag}
          name={data.name}
          uid={data.uid}
          logo={data.logo}
        />
      );
    case "badges":
      const badges: Badge[] =
        data.badges
          ?.map((appliedBadge: AppliedBadge) =>
            data.allBadges?.find(
              (badge: Badge) => badge.tag === appliedBadge.tag
            )
          )
          ?.filter((badge: Badge | undefined): badge is Badge =>
            Boolean(badge)
          ) ?? [];

      const hasBadges = badges.length > 0;

      return hasBadges ? (
        <BadgesGroup badges={badges} ariaLabel="Badges" mode="icon" />
      ) : null;
    default:
      return data?.[key];
  }
};

/// ---- for copy over data table ----
type GetCopyOverDataTableColumnsProps = {
  objectType?: GetDataObjectListObjectType | DataObjectType;
  objectTypeTranslatedUISchema?: DataObjectUISchemaRaw;
};

export const getCopyOverDataTableColumns = ({
  objectType,
  objectTypeTranslatedUISchema
}: GetCopyOverDataTableColumnsProps): TableUnifiedColumn[] => {
  const objectTypeListUISchema = objectType?.uiSchema?.list?.fields;
  const objectTypeFieldsUISchema = objectType?.uiSchema?.fields;
  const objectTypeSchemaFields = objectType?.schema.properties ?? {};

  const createColumnConfig = (key: string): TableUnifiedColumn => {
    return {
      header:
        objectTypeTranslatedUISchema?.fields?.[key]?.title ||
        objectTypeFieldsUISchema?.[key]?.title ||
        key,
      id: key,
      width: 150,
      cell: ({ row }: { row: { original: DataObjectInstance["data"] } }) => (
        <DataObjectTableFieldsCell
          uISchema={objectTypeFieldsUISchema?.[key]}
          schema={objectTypeSchemaFields?.[key]}
          value={row.original?.[key]}
          translatedUISchema={objectTypeTranslatedUISchema?.fields?.[key]}
          fieldIndicator={row.original?.indicators?.[key]}
        />
      )
    };
  };

  const fieldKeys = objectTypeListUISchema?.length
    ? objectTypeListUISchema
    : Object.keys(objectTypeSchemaFields);

  const dynamicColumns: TableUnifiedColumn[] =
    fieldKeys.map(createColumnConfig);

  return dynamicColumns;
};
/// ---- for copy over data table ----

type GetDataObjectTableColumnsProps = {
  objectType?: GetDataObjectListObjectType | DataObjectType;
  objectTypeTranslatedUISchema?: DataObjectUISchemaRaw;
  filtersByField?: FiltersByFieldMap | null;
  onSortChange?: (key: string, value?: SortDirection) => void;
  onFilterChange?: (
    key: string,
    value: string[] | string | DateRangeValuesType
  ) => void;
  customColumns?: readonly string[];
  allDefaultColumns: TableUnifiedColumn[];
  includedDefaultColumns: readonly DefaultColumnType[];
};

// this function is used to get the columns for the data object table
// we are adding date range filter for date and datetime fields when indexed is true
// we are adding sorting on all the indexed true columns where column type is sortable, SORTABLE_TYPES types are number, integer, date, datetime
// dynamic columns fields array can also include system columns like name, status, created_at, updated_at, so if the fieldKeys array includes these columns, they will be rendered as dynamic columns
// if system columns are not included in the fieldKeys array, they will be rendered as default columns in the start
// default columns are rendered only if they are not in the fieldKeys and are included in the includedDefaultColumns
// default columns are rendered only when it is included in includedDefaultColumns array, else we wont render it, we will also exclude it from the fieldKeys array as well as the allDefaultColumns array
export const getDataObjectTableColumns = ({
  objectType,
  objectTypeTranslatedUISchema,
  filtersByField,
  onSortChange,
  onFilterChange,
  customColumns,
  allDefaultColumns,
  includedDefaultColumns
}: GetDataObjectTableColumnsProps): {
  defaultColumns: TableUnifiedColumn[];
  dynamicColumns: TableUnifiedColumn[];
} => {
  const objectTypeListUISchema = objectType?.uiSchema?.list?.fields;
  const objectTypeFieldsUISchema = objectType?.uiSchema?.fields;
  const objectTypeSchemaFields = objectType?.schema.properties ?? {};

  const createColumnConfig = (key: string): TableUnifiedColumn => {
    const column = allDefaultColumns.find(column => column.id === key);
    if (column) return column;

    const isIndexed = objectTypeSchemaFields?.[key]?.indexed;
    const fieldType = objectTypeSchemaFields?.[key]?.type;

    const canSort =
      objectTypeSchemaFields?.[key]?.indexed &&
      SORTABLE_TYPES.includes(fieldType);

    const { canFilter, filterType } =
      (fieldType === "date" || fieldType === "datetime") && isIndexed
        ? {
            canFilter: true,
            filterType: FILTER_TYPES.DATE_RANGE
          }
        : {
            canFilter: !!filtersByField?.[key],
            filterType: filtersByField?.[key]?.type
          };

    return {
      header:
        objectTypeTranslatedUISchema?.fields?.[key]?.title ||
        objectTypeFieldsUISchema?.[key]?.title ||
        key,
      id: key,
      width: 150,
      canFilter,
      filterType,
      filterProps: {
        options: filtersByField?.[key]?.options,
        optionUrl: filtersByField?.[key]?.optionUrl
      },
      canSort,
      onSort: (value?: SortDirection) => {
        onSortChange?.(key, value);
      },
      onFilter: (value: string[] | string | DateRangeValuesType) => {
        if (
          (fieldType === "datetime" || fieldType === "date") &&
          typeof value === "object" &&
          "start_date" in value
        ) {
          const newDateRange = getDateTimeRangeValues(value, fieldType);
          onFilterChange?.(key, newDateRange);
        } else {
          onFilterChange?.(key, value);
        }
      },
      cell: ({ row }: { row: { original: DataObjectInstance["data"] } }) => (
        <DataObjectTableFieldsCell
          uISchema={objectTypeFieldsUISchema?.[key]}
          schema={objectTypeSchemaFields?.[key]}
          value={row.original?.[key]}
          translatedUISchema={objectTypeTranslatedUISchema?.fields?.[key]}
          fieldIndicator={row.original?.indicators?.[key]}
        />
      )
    };
  };

  const fieldKeys = customColumns
    ? customColumns
    : objectTypeListUISchema?.length
      ? objectTypeListUISchema
      : Object.keys(objectTypeSchemaFields);

  // if the key is not in the DEFAULT_INCLUDED_COLUMNS render it, and if it is in the DEFAULT_INCLUDED_COLUMNS it should be rendered only if it is included in the includedDefaultColumns
  const dynamicColumns: TableUnifiedColumn[] = fieldKeys
    .filter(key => {
      if (!DEFAULT_INCLUDED_COLUMNS.includes(key as DefaultColumnType))
        return true;
      return includedDefaultColumns.includes(key as DefaultColumnType);
    })
    .map(createColumnConfig);

  // default columns should be rendered only if they are not in the fieldKeys and are included in the includedDefaultColumns
  const defaultColumns: TableUnifiedColumn[] = Object.values(
    allDefaultColumns
  ).filter(
    column =>
      !fieldKeys.includes(column.id) &&
      includedDefaultColumns.includes(column.id as DefaultColumnType)
  );

  return { defaultColumns, dynamicColumns };
};

export const getDataObjectTableRows = (
  objectInstances: GetDataObjectListResult[] | DataObjectInstance[]
) => {
  return objectInstances?.map(objectInstance => ({
    ...objectInstance.data,
    name: objectInstance.name,
    logo: objectInstance.logo,
    uid:
      "objectUid" in objectInstance
        ? objectInstance.objectUid
        : objectInstance.uid,
    id:
      "objectId" in objectInstance
        ? objectInstance.objectId
        : objectInstance.id,
    status: objectInstance.status,
    createdAt: objectInstance.createdAt,
    updatedAt: objectInstance.updatedAt,
    badges: objectInstance.appliedBadges,
    indicators:
      "indicators" in objectInstance ? objectInstance.indicators : null
  }));
};

export const useCreateFilterParams = (
  filters: Record<
    string,
    string[] | string | number | DateRangeValuesType | undefined
  >
) => {
  const result: Record<string, string | undefined> = {};
  for (const key in filters) {
    const fieldValue = filters[key];
    if (filters.hasOwnProperty(key) && Array.isArray(fieldValue))
      result[key + "__in"] = fieldValue.join(",");

    if (filters.hasOwnProperty(key) && typeof fieldValue === "string")
      result[key] = fieldValue;

    if (
      filters.hasOwnProperty(key) &&
      typeof fieldValue === "object" &&
      "start_date" in fieldValue
    ) {
      const keySuffixBefore = fieldValue.is_date_time
        ? "__date_range_before"
        : "__range_before";

      const keySuffixAfter = fieldValue.is_date_time
        ? "__date_range_after"
        : "__range_after";

      result[key + keySuffixBefore] = fieldValue.end_date ?? undefined;
      result[key + keySuffixAfter] = fieldValue.start_date ?? undefined;
    }
  }

  return result;
};
